import { createSlice, createListenerMiddleware } from '@reduxjs/toolkit';

import { FUEL_TYPES } from 'config/app-config';
import type { RootState, AppStartListening } from 'state/store';

export type ConsumptionState = {
  [FUEL_TYPES.ELECTRIC]?: null | number;
  [FUEL_TYPES.GAS]?: null | number;
  mpan: Record<string, number | undefined>;
  mprn: Record<string, number | undefined>;
};

export type TSetMpxnConsumptionPayload = {
  key: string;
  value?: number;
  type: 'mprn' | 'mpan';
};

export const initialState: ConsumptionState = {
  [FUEL_TYPES.ELECTRIC]: null,
  [FUEL_TYPES.GAS]: null,
  mpan: {},
  mprn: {}
};

export const slice = createSlice({
  name: 'consumption',
  initialState,
  reducers: {
    setInitialState: (state) => {
      state[FUEL_TYPES.ELECTRIC] = initialState[FUEL_TYPES.ELECTRIC];
      state[FUEL_TYPES.GAS] = initialState[FUEL_TYPES.GAS];
      state.mpan = initialState.mpan;
      state.mprn = initialState.mprn;
    },
    setConsumption: (state, { payload }) => {
      Object.entries(payload).forEach(([key, value]) => {
        state[key] = value;
      });
    },
    setMpxnConsumption: (
      state,
      {
        payload
      }: {
        payload: TSetMpxnConsumptionPayload;
      }
    ) => {
      const { key, value, type } = payload;

      state[`${type}`][`${key}`] = value;
    }
  }
});

export const {
  setConsumption,
  setInitialState,
  setMpxnConsumption
} = slice.actions;

export const selectSlice = (state: RootState) => state[slice.name];
export const selectMpxnConsumption = (state: RootState) => {
  const mpan = selectSlice(state).mpan;
  const mprn = selectSlice(state).mprn;

  return {
    mpan,
    mprn
  };
};

// middleware
export const calculateTotalConsumptionMiddleware = createListenerMiddleware();
const calculateMpanTotalConsumptionMiddlewareStartListening = calculateTotalConsumptionMiddleware.startListening as AppStartListening;

calculateMpanTotalConsumptionMiddlewareStartListening({
  predicate: (action) => {
    return action.type === slice.actions.setMpxnConsumption.type;
  },
  effect: (_action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const totalElectricity = Object.values(state.consumption.mpan).reduce(
      (sum: number, value = 0) => sum + value,
      0
    );
    const totalGas = Object.values(state.consumption.mprn).reduce(
      (sum: number, value = 0) => sum + value,
      0
    );

    listenerApi.dispatch(
      setConsumption({
        [FUEL_TYPES.ELECTRIC]: totalElectricity,
        [FUEL_TYPES.GAS]: totalGas
      })
    );
  }
});

export default slice.reducer;
